import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/AppShell';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import TasksPage from './pages/TasksPage';
import UsersPage from './pages/UsersPage';
import MilestonesPage from './pages/MilestonesPage';
import ProgrammesPage from './pages/ProgrammesPage';
import ProgrammeDetailPage from './pages/ProgrammeDetailPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import MilestoneDetailPage from './pages/MilestoneDetailPage';
import TaskDetailPage from './pages/TaskDetailPage';
import ReportsPage from './pages/ReportsPage';
import WeeklyReportPage from './pages/WeeklyReportPage';
import WeeklyPlannerPage from './pages/WeeklyPlannerPage';
import TimesheetsPage from './pages/TimesheetsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import ProfilePage from './pages/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import ChangelogPage from './pages/ChangelogPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PasswordResetPage from './pages/PasswordResetPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import PendingRegistrationsPage from './pages/admin/PendingRegistrationsPage';
import ProjectRolesPage from './pages/admin/ProjectRolesPage';
import ProjectTeamPage from './pages/admin/ProjectTeamPage';
import ApplicationSettingsPage from './pages/admin/ApplicationSettingsPage';
import NotificationAdminPage from './pages/admin/NotificationAdminPage';
import EmailConfigPage from './pages/admin/EmailConfigPage';
import AuthenticationPage from './pages/admin/AuthenticationPage';
import SettingsPage from './pages/admin/SettingsPage';
import SystemRolesPage from './pages/admin/SystemRolesPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import TenantsPage from './pages/admin/TenantsPage';
import Footer from './components/Footer';
import { useNavigationAnalytics } from './hooks/useNavigationAnalytics';
import { useSSENotifications } from './hooks/useSSE';
import ApiCompatBanner from './components/ApiCompatBanner';
import './styles/global.css';


const AppContent: React.FC = () => {
    // Initialize navigation analytics
    useNavigationAnalytics();
    
    // Initialize SSE real-time notifications (only when authenticated)
    // Hook must be called unconditionally - the hook itself handles authentication state
    useSSENotifications();
    
    return (
        <AppShell>
            <div className="min-h-screen flex flex-col">
                <ApiCompatBanner />
                <main className="flex-1 p-6">
                    <Routes>
                        {/* Protected routes - require authentication */}
                        <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
                        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
                        <Route path="/programmes" element={<ProtectedRoute><ProgrammesPage /></ProtectedRoute>} />
                        <Route path="/programmes/:id" element={<ProtectedRoute><ProgrammeDetailPage /></ProtectedRoute>} />
                        <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
                        <Route path="/tasks/:id" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
                        <Route path="/milestones" element={<ProtectedRoute><MilestonesPage /></ProtectedRoute>} />
                        <Route path="/milestones/:id" element={<ProtectedRoute><MilestoneDetailPage /></ProtectedRoute>} />
                        <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
                        <Route path="/weekly-planner" element={<ProtectedRoute><WeeklyPlannerPage /></ProtectedRoute>} />
                        <Route path="/timesheets" element={<ProtectedRoute><TimesheetsPage /></ProtectedRoute>} />
                        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
                        <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReportPage /></ProtectedRoute>} />
                        <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                        
                        {/* Public routes - authentication pages */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<PasswordResetPage />} />
                        {/* Multi-tenancy: the only route by which a client user joins. */}
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/changelog" element={<ChangelogPage />} />
                        
                        {/* Admin routes - require admin role */}
                        <Route path="/admin/pending-registrations" element={<ProtectedRoute requireTenantAdmin><PendingRegistrationsPage /></ProtectedRoute>} />
                        <Route path="/admin/users" element={<ProtectedRoute requireTenantAdmin><UserManagementPage /></ProtectedRoute>} />
                        <Route path="/admin/system-roles" element={<ProtectedRoute requireTenantAdmin><SystemRolesPage /></ProtectedRoute>} />
                        {/* Role Management consolidated into Manage Access on /admin/users; keep old links working */}
                        <Route path="/admin/role-management" element={<Navigate to="/admin/users" replace />} />
                        <Route path="/admin/project-roles" element={<ProtectedRoute requireTenantAdmin><ProjectRolesPage /></ProtectedRoute>} />
                        <Route path="/admin/project-team" element={<ProtectedRoute requireTenantAdmin><ProjectTeamPage /></ProtectedRoute>} />
                        <Route path="/admin/application-settings" element={<ProtectedRoute requireTenantAdmin><ApplicationSettingsPage /></ProtectedRoute>} />
                        <Route path="/admin/notification-preferences" element={<ProtectedRoute requireTenantAdmin><NotificationAdminPage /></ProtectedRoute>} />
                        <Route path="/admin/email-configuration" element={<ProtectedRoute requirePlatformAdmin><EmailConfigPage /></ProtectedRoute>} />
                        <Route path="/admin/authentication" element={<ProtectedRoute requirePlatformAdmin><AuthenticationPage /></ProtectedRoute>} />
                        {/* The admin hub is a menu of links, each of which keeps its own guard,
                            so both tiers may open it — they see different cards inside. */}
                        <Route path="/admin/settings" element={<ProtectedRoute requireAnyAdmin><SettingsPage /></ProtectedRoute>} />
                        <Route path="/admin/audit-logs" element={<ProtectedRoute requirePlatformAdmin><AuditLogsPage /></ProtectedRoute>} />
                        {/* Multi-tenancy: workspace + seat administration (Akhile only) */}
                        <Route path="/admin/tenants" element={<ProtectedRoute requirePlatformAdmin><TenantsPage /></ProtectedRoute>} />
                        
                        {/* Catch-all route for 404 - must be last */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </main>
                <Footer />
            </div>
        </AppShell>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <AuthProvider>
                <Router>
                    <AppContent />
                    {/* Toast Notifications */}
                    <Toaster
                        position="top-right"
                        reverseOrder={false}
                        gutter={8}
                        toastOptions={{
                            duration: 4000,
                            style: {
                                borderRadius: '12px',
                                padding: '16px',
                                fontSize: '14px',
                                fontWeight: '500',
                            },
                        }}
                    />
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
};

export default App;